import { createContext, useContext } from 'react'

/**
 * How a node view uploads a file, without knowing anything about transport.
 *
 * A context rather than a prop, because the things that need to upload are
 * ProseMirror node views several layers below the component that owns the API
 * client and the toasts. Threading a callback down to them would mean every
 * node view taking a prop it does not use.
 *
 * Returning null rather than throwing: an upload that was refused — too large,
 * wrong type — has already been reported to the person who tried it, and a
 * node view has nothing useful to add.
 */
export interface Uploader {
  (file: File): Promise<{ url: string; altText: string | null } | null>
}

const UploadContext = createContext<Uploader | null>(null)

export const UploadProvider = UploadContext.Provider

/** null when uploading is unavailable — a read-only branch, or no session. */
export function useUploader(): Uploader | null {
  return useContext(UploadContext)
}

/**
 * The image files in a paste or a drop, if any.
 *
 * A screenshot on the clipboard arrives as a `File` with an empty name, which is
 * why the name is never used for anything. Non-image files are ignored rather
 * than refused: dragging a PDF onto a paragraph most likely means the person
 * wanted a link, and swallowing the drop would leave them with nothing.
 */
export function imageFilesFrom(data: DataTransfer | null): File[] {
  if (!data) return []
  return [...data.files].filter((f) => f.type.startsWith('image/'))
}
